import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Chip,
    Stack,
    IconButton,
    Tooltip
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useNavigate } from 'react-router-dom';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    const routes = {
        "Prayer": "/admin/public-prayers",
        "Testimony": "/admin/testimonies",
        "User Register": "/admin/users",
        "Organization Register": "/admin/organizations",
        "Rider": "/admin/drivers",
        "Generosity Giver Item": "/admin/giver-items",
        "Generosity New Item Request": "/admin/new-item-requests"
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const res = await fetch(`${API_URL}all-notifications`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();

            if (!res.ok || !data.success) {
                setError(data.message || "Failed to load notifications");
            } else {
                setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
            }
        } catch (err) {
            console.error("Network error", err);
            setError("Failed to load notifications");
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationClick = async (notification) => {
        try {
            const token = localStorage.getItem("token");

            // Mark notification as read
            await fetch(`${API_URL}readnotification`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: notification.id,
                    module_id: notification.module_id,
                    title: notification.title
                }),
            });

            // Navigate to the appropriate page
            const route = routes[notification.title];
            if (route) {
                navigate(route);
            }
        } catch (err) {
            console.error("Error handling notification", err);
        }
    };

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const now = new Date();
        const diffInMs = now - date;
        const diffInMinutes = Math.floor(diffInMs / 60000);
        const diffInHours = Math.floor(diffInMinutes / 60);
        const diffInDays = Math.floor(diffInHours / 24);

        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress sx={{ color: '#4CA1AF' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                pb: 2,
                borderBottom: '2px solid rgba(76, 161, 175, 0.3)'
            }}>
                <NotificationsActiveIcon sx={{ fontSize: 32, color: '#4CA1AF' }} />
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: '#FFFFFF',
                        textShadow: '0 2px 10px rgba(76, 161, 175, 0.3)'
                    }}
                >
                    All Notifications
                </Typography>
                {notifications.length > 0 && (
                    <Chip
                        label={`${notifications.length} notification${notifications.length !== 1 ? 's' : ''}`}
                        sx={{
                            background: 'rgba(76, 161, 175, 0.2)',
                            color: '#4CA1AF',
                            fontWeight: 600,
                            border: '1px solid rgba(76, 161, 175, 0.3)'
                        }}
                    />
                )}
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        mb: 3,
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#EF4444',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        '& .MuiAlert-icon': {
                            color: '#EF4444'
                        }
                    }}
                >
                    {error}
                </Alert>
            )}

            {/* Notifications List */}
            {notifications.length === 0 ? (
                <Card sx={{
                    background: 'rgba(30, 41, 59, 0.6)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 3,
                    textAlign: 'center',
                    py: 8
                }}>
                    <CardContent>
                        <NotificationsIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            No notifications yet
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}>
                            You're all caught up!
                        </Typography>
                    </CardContent>
                </Card>
            ) : (
                <Stack spacing={2}>
                    {notifications.map((notification) => (
                        <Card
                            key={notification.id}
                            onClick={() => handleNotificationClick(notification)}
                            sx={{
                                background: notification.is_read
                                    ? 'rgba(30, 41, 59, 0.4)'
                                    : 'rgba(76, 161, 175, 0.1)',
                                backdropFilter: 'blur(10px)',
                                border: notification.is_read
                                    ? '1px solid rgba(255,255,255,0.05)'
                                    : '1px solid rgba(76, 161, 175, 0.3)',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 24px rgba(76, 161, 175, 0.2)',
                                    background: 'rgba(76, 161, 175, 0.15)',
                                    borderColor: 'rgba(76, 161, 175, 0.5)',
                                }
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    {/* Icon */}
                                    <Box sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12px',
                                        background: notification.is_read
                                            ? 'rgba(255,255,255,0.05)'
                                            : 'rgba(76, 161, 175, 0.2)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}>
                                        <NotificationsIcon sx={{
                                            color: notification.is_read
                                                ? 'rgba(255,255,255,0.4)'
                                                : '#4CA1AF',
                                            fontSize: 24
                                        }} />
                                    </Box>

                                    {/* Content */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: notification.is_read ? 500 : 700,
                                                    color: '#FFFFFF',
                                                    fontSize: '1rem'
                                                }}
                                            >
                                                {notification.title}
                                            </Typography>
                                            {!notification.is_read && (
                                                <Box sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    background: '#EF4444',
                                                    boxShadow: '0 0 8px rgba(239, 68, 68, 0.6)'
                                                }} />
                                            )}
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'rgba(255,255,255,0.7)',
                                                mb: 1,
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {notification.message}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: 'rgba(255,255,255,0.5)',
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {notification.timestamp}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}
        </Box>
    );
};

export default Notifications;
